/**
 * ActiveRideDetail — main right-rail detail panel shown to a passenger
 * while a ride is searching / in-progress / completed-but-unpaid.
 *
 * iter-79 decomposition slice 4 of Passenger.jsx.
 *
 * Strict isolation contract (matches Driver.jsx / Slices 1-3 playbook):
 *   - Zero behavioural change vs. the original inline definition.
 *   - All props, helpers, and rendering logic preserved exactly.
 *   - No dispatch / state-machine / janitor / offer-routing logic touched.
 *
 * Props:
 *   ride           — active ride object
 *   dispatchStatus — live dispatch poller payload
 *   onCancel       — () => void
 *   onPayCard      — () => void
 *   paying         — boolean (stripe redirect in-flight)
 *   onMessage      — () => void (opens RideChatDialog)
 */
import React from "react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Car, Zap, Phone, MessageSquare, Receipt, Briefcase, CreditCard, X } from "lucide-react";
import { FareMeterPanel } from "../FareMeterPanel";
import { Stat } from "./BookingFormHelpers";
import { FareBreakdown } from "./FareBreakdown";
import { DispatchStageMessage, pillFor } from "./RideStatusPanel";

export const ActiveRideDetail = ({ ride, dispatchStatus, onCancel, onPayCard, paying, onMessage }) => {
  const s = pillFor(ride);
  // Slice 2 — masked voice call to the driver. The passenger dials a SHARED
  // proxy number (never the driver's real number); the Twilio voice webhook
  // identifies the passenger by caller ID and bridges to the driver.
  const callDriver = async () => {
    try {
      const res = await api.get(`/passenger/rides/${ride.id}/call`);
      const data = res?.data || {};
      if (data.available && data.call_number) {
        toast.success("Connecting through Crystal Dispatch…", { description: data.note });
        window.location.href = `tel:${data.call_number}`;
      } else {
        toast.message(data.reason || "Calling isn't available right now.");
      }
    } catch (e) {
      toast.error("Couldn't start the call. Please try again.");
    }
  };
  const isCardUnpaid = ride.status === "completed" && ride.payment_method === "card" && ride.payment_status !== "paid";
  // Phase-first derivation — mirrors the Driver app. Backend `phase` is the
  // single source of truth; we fall back to legacy `status` only when phase
  // is somehow absent (older docs pre-backfill).
  const phase = ride.phase || null;
  const phaseHeadline = (() => {
    switch (phase) {
      case "scheduled":             return "Booked for later";
      case "dispatching":           return "Finding you a driver…";
      case "offered":               return "Driver found · confirming…";
      case "assigned":              return "Driver on the way";
      case "driver_en_route":       return "Driver on the way";
      case "driver_arrived_pickup": return "Your driver is here";
      case "trip_in_progress":      return "On your way";
      case "driver_arrived_dropoff":return "Arriving now";
      case "completed":             return "Trip complete";
      case "cancelled":             return "Trip cancelled";
      default:
        // Legacy status fallback (used only if phase is missing).
        if (ride.status === "searching" || ride.status === "offered") return "Hold tight…";
        if (ride.status === "accepted") return "Driver on the way";
        if (ride.status === "in_progress") return "On your way";
        if (ride.status === "completed") return "Trip complete";
        return "Trip ended";
    }
  })();
  // Driver-arrived banner: prefer the canonical phase but keep the
  // `driver_arrived_at` field as a backward-compat secondary signal so we
  // don't regress the banner timing for any in-flight rides.
  const driverHasArrived =
    phase === "driver_arrived_pickup" ||
    (ride.status === "accepted" && !!ride.driver_arrived_at);
  // Pre-arrival heads-up: only on the pickup leg, never once arrived.
  const driverIsNearby =
    (phase === "assigned" || phase === "driver_en_route") &&
    !!ride.driver_nearby_at &&
    !driverHasArrived;
  return (
    <div className="space-y-4" data-testid="active-ride-detail">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className={`w-2.5 h-2.5 rounded-full ${s.pulse ? "pulse-warning" : ""}`} style={{ backgroundColor: s.color }} />
          <span className="label-mini">{s.label}</span>
        </div>
        <h2 className="font-heading text-3xl font-black tracking-tight" data-testid="active-ride-headline">
          {phaseHeadline}
        </h2>
        {phase && (
          <div className="label-mini text-muted-foreground text-[10px] mt-1 opacity-60" data-testid="active-ride-phase-tag">
            phase {phase}
          </div>
        )}
      </div>

      {/* Driver-arrived banner — fires when the driver crosses the 100m pickup
          geofence (or manually taps "I've arrived"). Designed to be impossible
          to miss so the passenger heads outside straight away. */}
      {driverHasArrived && (
        <div
          className="border-2 border-[#10B981] bg-[#ECFDF5] p-4 flex items-start gap-3"
          data-testid="driver-arrived-banner"
        >
          <div className="w-9 h-9 rounded-full bg-[#10B981] flex items-center justify-center flex-shrink-0">
            <Car className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-mini text-[#065F46]">DRIVER HAS ARRIVED</div>
            <div className="font-heading font-black text-lg leading-tight mt-0.5">
              {ride.driver?.name ? `${ride.driver.name} is here — head outside` : "Your driver is here — head outside"}
            </div>
            {ride.driver?.vehicle_plate && (
              <div className="text-xs text-muted-foreground mt-1 font-mono-tab">
                Look for {ride.driver.vehicle_make || "the car"} · {ride.driver.vehicle_plate}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pre-arrival heads-up — fires when the driver enters the 500m geofence
          but isn't AT the pickup yet. Suppressed once driverHasArrived flips
          true so we never stack two banners. */}
      {driverIsNearby && (
        <div
          className="border-2 border-[#FFC107] bg-[#FFFBEB] p-4 flex items-start gap-3"
          data-testid="driver-nearby-banner"
        >
          <div className="w-9 h-9 rounded-full bg-[#FFC107] flex items-center justify-center flex-shrink-0">
            <Car className="w-5 h-5 text-[#0A0A0A]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-mini text-[#92400E]">~2 MINUTES AWAY</div>
            <div className="font-heading font-black text-lg leading-tight mt-0.5">
              {ride.driver?.name ? `${ride.driver.name} is nearly here` : "Your driver is nearly here"}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Grab your things — we'll ping you again the moment they pull up.
            </div>
          </div>
        </div>
      )}

      <DispatchStageMessage ride={ride} dispatchStatus={dispatchStatus} />

      {/* Passenger-side Estimated live fare — visible only during
          trip_in_progress. Read-only display (no controls); the backend
          enforces passenger ownership of the ride. Regulatory wording:
          NEVER "taxi meter" / "metered fare" — the panel labels itself
          "Estimated live fare" and surfaces the disclaimer. After
          completion the same endpoint returns the final fare from the
          ride record, so this panel becomes a read-only receipt — but we
          conditionally hide it once the ride completes because the
          existing `active-receipt` block below already shows the final
          breakdown more prominently. */}
      {phase === "trip_in_progress" && (
        <FareMeterPanel
          rideId={ride.id}
          startedAt={ride.started_at}
          fallbackEstimate={ride.fare || ride.estimated_fare}
          endpoint="/passenger/live-fare"
          variant="passenger"
        />
      )}

      {ride.surge_multiplier > 1.0 && (
        <div className="flex items-center gap-2 text-xs text-[#92400E]">
          <Zap className="w-3.5 h-3.5" /> Surge {ride.surge_multiplier}× applied
        </div>
      )}

      <div className="space-y-3 border border-border p-4">
        <div className="flex items-start gap-3">
          <div className="w-3 h-3 rounded-full bg-[#10B981] mt-1 flex-shrink-0" />
          <div>
            <div className="label-mini text-muted-foreground">PICKUP</div>
            <div className="text-sm font-semibold">{ride.pickup_address}</div>
          </div>
        </div>
        {(ride.stops || []).map((st, i) => (
          <React.Fragment key={i}>
            <div className="ml-1.5 border-l-2 border-dashed border-border h-4" />
            <div className="flex items-start gap-3" data-testid={`active-ride-stop-${i}`}>
              <div className="w-3 h-3 rounded-full bg-[#002FA7] mt-1 flex-shrink-0" />
              <div>
                <div className="label-mini text-muted-foreground">STOP {i + 1}</div>
                <div className="text-sm font-semibold">{st.address}</div>
              </div>
            </div>
          </React.Fragment>
        ))}
        <div className="ml-1.5 border-l-2 border-dashed border-border h-4" />
        <div className="flex items-start gap-3">
          <div className="w-3 h-3 rounded-full bg-[#EF4444] mt-1 flex-shrink-0" />
          <div>
            <div className="label-mini text-muted-foreground">DROP-OFF</div>
            <div className="text-sm font-semibold">{ride.dropoff_address}</div>
          </div>
        </div>
      </div>

      {ride.driver && (
        <div className="border border-border p-4 bg-secondary/30" data-testid="driver-info-card">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#FFC107] flex items-center justify-center font-bold">
              {ride.driver.name?.[0]}
            </div>
            <div className="flex-1">
              <div className="font-semibold">{ride.driver.name}</div>
              <div className="text-xs text-muted-foreground">{ride.driver.vehicle_make}</div>
            </div>
            <div className="text-right">
              <div className="label-mini text-muted-foreground">PLATE</div>
              <div className="font-mono-tab font-bold">{ride.driver.vehicle_plate}</div>
            </div>
          </div>
          {/* Contact row. CALL bridges the passenger↔driver through Twilio
              voice masking (Slice 2): the passenger dials a shared proxy number
              and the webhook connects the driver — the driver's real number is
              NEVER shown (privacy contract). MESSAGE opens the ride chat. */}
          <div className="mt-3 pt-3 border-t border-border flex gap-2" data-testid="ride-contact-row">
            <Button
              variant="outline"
              size="sm"
              onClick={callDriver}
              className="text-xs h-8 flex-1"
              data-testid="ride-call-btn"
            >
              <Phone className="w-3 h-3 mr-1.5" /> CALL
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={onMessage}
              className="text-xs h-8 flex-1 bg-[#0A0A0A] hover:bg-[#0A0A0A]/90"
              data-testid="ride-message-btn"
            >
              <MessageSquare className="w-3 h-3 mr-1.5" /> MESSAGE
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2">
        <Stat label="DISTANCE" value={`${ride.distance_mi ?? ride.distance_km} mi`} />
        <Stat label={ride.status === "completed" ? "FINAL FARE" : "EST. FARE"} value={`£${ride.fare || ride.estimated_fare}`} accent="#FFC107" />
        <Stat label="PAYMENT" value={(ride.payment_method || "").toUpperCase()} />
      </div>

      {ride.fare_breakdown && (
        <div className="border border-border p-3 bg-secondary/30" data-testid="active-receipt">
          <div className="label-mini text-muted-foreground mb-2 flex items-center gap-1">
            <Receipt className="w-3 h-3" /> RECEIPT · VAT BREAKDOWN
          </div>
          <FareBreakdown b={ride.fare_breakdown} compact />
        </div>
      )}

      {ride.is_account_ride && ride.status === "completed" && (
        <div className="border border-[#002FA7] bg-[#EFF3FF] p-3 flex items-center gap-2 text-sm" data-testid="account-invoiced-badge">
          <Briefcase className="w-4 h-4 text-[#002FA7]" />
          <span className="font-semibold">Invoiced to {ride.account_company || "your account"}</span> — no payment needed now.
        </div>
      )}

      {isCardUnpaid && !ride.is_account_ride && (
        <Button onClick={onPayCard} disabled={paying} className="w-full bg-[#002FA7] hover:bg-[#001f6e]" size="lg" data-testid="pay-with-card-btn">
          <CreditCard className="w-4 h-4 mr-2" /> {paying ? "Redirecting…" : `Pay £${ride.fare} with card`}
        </Button>
      )}

      {ride.status !== "in_progress" && ride.status !== "completed" && (
        <Button variant="outline" onClick={onCancel} className="w-full" data-testid="cancel-ride-detail-btn">
          <X className="w-4 h-4 mr-1.5" /> Cancel ride
        </Button>
      )}
    </div>
  );
};

export default ActiveRideDetail;
